import {useEffect, useMemo, useRef, useState} from "react";
import moment from "moment/moment";
import {useSnackbar} from "notistack";
import {useAuthContext} from "../../../auth/useAuthContext";
import {useSelector} from "react-redux";
import {DIRECTION_NAME, INVOICE_TYPE, PAYMENT_TYPE, SETTINGS_PAYMENTS, TRANSACTION_TYPE} from "../../../constants";
import {allSeats} from "../../../api/trip";
import {getCashierName} from "../../../utils/other";
import {Invoice} from "../../../helper/invoice/Invoice";
import {calculateBasicOfItem, validateValues} from "../../../helper/other";
import {issueInvoiceVerificationApi} from "../../../api/invoice";
import {getSessionSelectedLocation} from "../../../helper/session";

export function useVerificationDialog({
                                          onClose,
                                          onSuccess,
                                          tripData,
                                          selectedSeats
                                      }) {

    const {enqueueSnackbar} = useSnackbar();
    const {user} = useAuthContext();

    const {allItemsList} = useSelector(state => state.items);
    const {roundedPrice} = useSelector(state => state.userSettings);
    const {currentTaxRates} = useSelector(state => state.taxCore);
    const {settings, userImage, silentPrint, printerSize, ticketLogo} = useSelector(state => state.settings);

    const [selectedPayments, setSelectedPayments] = useState([]);
    const [exchange, setExchange] = useState(0);
    const [cardNumbers, setCardNumbers] = useState([""]);
    const [loading, setLoading] = useState(false);
    const [availableSeats, setAvailableSeats] = useState([]);
    const [totalPassengers, setTotalPassengers] = useState(1);
    const [openSelect, setOpenSelect] = useState(false);
    const abortControllerRef = useRef(null);

    const item = useMemo(() => allItemsList.find(i => i.includeOnVerification === true), [allItemsList]);

    // Operateri kojima overa ne ide na fiskalizaciju ništa ne naplaćuju — server na kartu upisuje
    // cenu 0, pa i kupon mora da pokaže 0, bez obzira na cenu artikla u cenovniku.
    const noFiscalVerification = !!user?.verifyWithoutFiscal;
    const itemPrice = noFiscalVerification ? 0 : (item?.price || 0);
    const total = itemPrice;

    const roundTotal = useMemo(
        () => (roundedPrice ? Math.ceil(total / 10) * 10 : total) * Number(totalPassengers),
        [roundedPrice, total, totalPassengers]
    );

    const filteredPayments = useMemo(
        () => SETTINGS_PAYMENTS.filter(value => !!settings?.[value.name]),
        [settings]
    );

    const showExchange = useMemo(
        () => selectedPayments.length === 1 && selectedPayments[0].paymentType === PAYMENT_TYPE.cash && settings?.change,
        [selectedPayments, settings?.change]
    );

    const handleChangePassengers = (event) => {
        const next = event.target.value;
        setTotalPassengers(next);
        setCardNumbers(Array.from({length: next}, (_, i) => cardNumbers[i] || ""));
    };

    const handleCloseSelect = () => setOpenSelect(false);
    const handleOpenSelect = () => setOpenSelect(true);

    useEffect(() => {
        setSelectedPayments([{paymentType: PAYMENT_TYPE.cash, amount: roundTotal}]);
        setExchange(0);
    }, [roundTotal]);

    useEffect(() => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setLoading(true);

        allSeats({
            id: tripData.tripId,
            date: moment(tripData.departureTripTime).format("yyyy-MM-DD"),
            depId: tripData.depStationId,
            arrId: tripData.arrStationId,
        }, controller.signal)
            .then(({data}) => {
                let resultSeats = data;

                // Ako sedista NISU numerisana uzmi samo slobodna sedista
                if (!tripData?.alSeatNumbered) {
                    resultSeats = data.filter(seat =>
                        (seat.reserved === false || seat.reserved === null) &&
                        (seat.occupied === false || seat.occupied === null) &&
                        seat.reservationExpireSec === null
                    );
                }
                setAvailableSeats(resultSeats);
            })
            .catch((err) => {
                if (err?.code === "ERR_CANCELED") return;
                //enqueueSnackbar("Nema slobodnih mesta", { variant: "info" });
            })
            .finally(() => setLoading(false));

        return () => {
            controller.abort();
        };
    }, [tripData, enqueueSnackbar]);


    const handleMethodClick = (method, isDouble) => {
        if (isDouble) {
            setSelectedPayments([{paymentType: method.value, amount: roundTotal}]);
        } else {
            let arr = [...selectedPayments];
            const index = arr.findIndex(m => m.paymentType === method.value);
            if (index === -1) {
                arr.push({paymentType: method.value, amount: 0});
            } else {
                arr.splice(index, 1);
            }
            if (arr.length > 1) {
                arr = arr.map(v => ({...v, amount: 0}));
            } else {
                arr = arr.map(v => ({...v, amount: roundTotal}));
            }
            setSelectedPayments(arr);
        }
        setExchange(0);
    };

    const handleChangeAmount = (event, index) => {
        const {name, value} = event.target;
        const rawValue = value.replace(',', '.');
        let arr = [...selectedPayments];
        const sumOthers = arr
            .filter(m => m.paymentType !== name)
            .reduce((acc, {amount}) => acc + amount, 0);
        const amount = Number(Number(rawValue).toFixed(2));
        const capped = Number(Number(sumOthers + amount <= roundTotal ? amount : roundTotal - sumOthers).toFixed(2));
        arr[index] = {...arr[index], amount: showExchange ? amount : capped};
        if (showExchange) setExchange(Number(Number(amount - roundTotal).toFixed(2)));
        setSelectedPayments(arr);
    };

    const handleChangeCardNumber = (index, value) => {
        const updated = [...cardNumbers];
        updated[index] = value;
        setCardNumbers(updated);
    };

    const handleVerificationCard = async (e) => {
        try {
            setLoading(true);
            e.preventDefault();

            if (cardNumbers.some((num) => !num?.trim())) {
                enqueueSnackbar("Sva polja za broj karte moraju biti popunjena.", {variant: "warning"});
                return;
            }

            const uniqueNumbers = new Set(cardNumbers);
            if (uniqueNumbers.size !== cardNumbers.length) {
                enqueueSnackbar("Brojevi karata moraju biti jedinstveni.", {variant: "warning"});
                return;
            }

            if (!item) {
                enqueueSnackbar("Ne postoji proizvod/usluga za izdavanje!", {variant: "warning"});
                return;
            }

            let sumAll = selectedPayments.reduce((acc, {amount}) => acc + amount, 0);
            if (showExchange) sumAll = sumAll - exchange;
            if (sumAll !== roundTotal) {
                enqueueSnackbar("Zbir plaćanja mora biti isti kao i ukupan iznos", {variant: "warning"});
                return;
            }

            if (tripData?.alSeatNumbered) {
                if (!selectedSeats?.length || selectedSeats.length < Number(totalPassengers)) {
                    enqueueSnackbar("Izaberite sedišta za sve putnike.", {variant: "warning"});
                    return;
                }
            }

            const idArray = cardNumbers.map((number) => (number == null ? "" : String(number)));
            const base = calculateBasicOfItem({
                ...item,
                unitPrice: itemPrice,
                quantity: 1
            }, currentTaxRates);

            let normalizedTickets = idArray.map((id, index) => {
                const chosen = selectedSeats?.[index];
                // TODO dodati validacije da su obavezna polja
                // TODO id treba da bude string ne samo number
                // TODO da se prosledjuje alotman vozilo i sediste i kad nije numerisan
                return {
                    externalNumber: id,
                    tripId: tripData.tripId,
                    departureStationId: tripData.depStationId,
                    arrivalStationId: tripData.arrStationId,
                    journeyDate: moment(tripData.departureTripTime).toISOString(),
                    seat: tripData?.alSeatNumbered ? chosen?.seat : availableSeats[index].seat,
                    vehicleId: tripData?.alSeatNumbered ? chosen?.vehicleId : availableSeats[index].vehicleId,
                    allotmentId: chosen?.allotmentId ? chosen?.allotmentId : availableSeats[index].alId,
                    itemName: item.name,
                    cashier: getCashierName(),
                    isReturnTicket: true,
                    price: itemPrice,
                    ticketTypeId: null,
                    labels: JSON.stringify([item.vat]),
                };
            });

            const invoice = new Invoice();
            invoice.setItems(idArray.map((id) => ({
                ...item,
                totalAmount: itemPrice,
                name: item.name,
                quantity: 1,
                labels: [item.vat],
                unitPrice: itemPrice,
                baseAmount: base,
                pdv: itemPrice - base,
                cashier: getCashierName(),
                id,
                salesTime: moment().format("YYYY-MM-DD HH:mm:ss"),
            })));

            invoice.setRoundedPrice = roundedPrice;
            let payment = selectedPayments;
            if (selectedPayments.length === 1) {
                payment = selectedPayments.map(v => ({...v, amount: roundTotal}));
            }
            // Kad se ne naplaćuje, iznos je 0 — inače bi provera načina plaćanja odbacila nulti red.
            invoice.setPayments(payment, null, null, noFiscalVerification);
            invoice.setInvoiceType(INVOICE_TYPE.normal);
            invoice.setTransactionType(TRANSACTION_TYPE.sale);
            invoice.setSilentPrint(silentPrint);
            invoice.setPrinterSize(printerSize);
            invoice.setLogo(userImage);
            invoice.setTicketLogo(ticketLogo);
            const localExchange = exchange < 0 ? 0 : exchange;
            invoice.setExchange(localExchange || 0);
            invoice.setPrintLogoInvoice(settings?.printLogoInvoice || "");
            invoice.setPrintLogoTicket(settings?.printLogoTicket || "");

            const toStr = (v) => (v == null ? null : String(v));
            const internalData = validateValues({
                client: "web",
                selectedLine: toStr(tripData.lineId || null),
                issuedFor: toStr(tripData.issuedFor?.tin || null),
                operatorId: tripData?.operatorId || null, // Integer u DTO
                jid: toStr(getSessionSelectedLocation()?.JID || null),
                startStation: toStr(tripData.depStationId ?? null),
                endStation: toStr(tripData.arrStationId ?? null),
                departureTripTime: toStr(tripData.departureTripTime ?? null),
                cardNumber: idArray.filter((s) => s !== ""),
                directionType: toStr(DIRECTION_NAME[1]),
            });
            if (internalData) invoice.setInternalData(internalData);

            if (tripData?.linkId) {
                normalizedTickets = normalizedTickets.map(t => ({...t, linkId: tripData.linkId}));
            }

            const invoiceResponse = (await issueInvoiceVerificationApi({
                invoice: {internalData: invoice.internalData, ...invoice.invoice},
                verificationDtos: normalizedTickets,
                isOnline: false
            })).data;

            const verificationOBJList = invoiceResponse.tickets.map((o, index) => ({
                arrStationName: tripData.arrStationName,
                depStationName: tripData.depStationName,
                platformNumber: tripData.platformNumber,
                carrier: tripData.carrier,
                cardNumber: idArray[index],
                ticketId: o.id,
                departureTripTime: tripData.departureTripTime,
                seat: tripData.alSeatNumbered ? o.seatReserved : "NN",
            }));

            await invoice.printVerification(verificationOBJList, user);
            if (invoiceResponse.taxCoreInvoice) {
                await invoice.printInvoice(invoice.invoice, invoiceResponse.taxCoreInvoice);
            }

            onSuccess?.();
            onClose?.();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("handleVerificationCard", error);
            enqueueSnackbar(typeof error === "string" ? error : (error?.data?.message || "Greška pri fiskalizaciji"), {variant: "warning"});
        } finally {
            setLoading(false);
        }
    };

    const getLabel = (method) => SETTINGS_PAYMENTS.find(m => m.value === method)?.displayName;

    return {
        // state
        loading,
        totalPassengers,
        openSelect,
        selectedPayments,
        exchange,
        cardNumbers,
        availableSeats,

        // derived
        roundTotal,
        filteredPayments,

        // handlers
        handleChangePassengers,
        handleOpenSelect,
        handleCloseSelect,
        handleMethodClick,
        handleChangeAmount,
        handleChangeCardNumber,
        handleVerificationCard,

        // utils
        getLabel,
    };
}
